import dayjs from 'dayjs';
import _ from 'lodash';
import { pinyin } from 'pinyin-pro';
import { menuStore } from '@/store/modules/menuUse.js';
import useI18n from "@/i18n/index.js";
const { t } = useI18n.global;

/**
 * 全局时间格式化标准
 */
export const dateFormat = "MM-DD-YYYY";
export const dateValueFormat = "YYYY-MM-DD";

/**
 * 金额过滤
 * @param {Number} money 金额
 * @param {Number} decimals 小数位数
 * @param {String} prefix 前缀（默认¥）
 * @param {String} suffix 后缀
 * @param {Boolean} isCut 是否截断，仅在小数有效
 * @param {Number} cutDecimals 截断位数，默认decimals
 * @param {Boolean} isThousand 千位分割
 * @return {String} 格式化后的金额
 */
export function formatMoney(
  money,
  decimals = 2,
  prefix = t('common.pricePrefix'),
  suffix = '',
  isCut = false,
  cutDecimals,
  isThousand = true,
) {
  const data = Number(money.toString().replace(/[^\d.-]/g, '')); // 转为数字
  let floatPart = '.' + ''.padEnd(decimals, '0'); // .00
  let isF = false; // 是否是负数
  if (!data || data === '0') return prefix + '0' + floatPart + suffix; // 0.00
  if (data < 0) isF = true;
  const intPart = Math.trunc(data); // 整数位
  const reg = /(\d)(?=(?:\d{3})+$)/g;
  const strIntPart = intPart.toString(); // 转为字符串
  const intPartFormat = strIntPart.replace(reg, '$1,'); // 千位分割
  const lastIntPart = isThousand ? intPartFormat : strIntPart;
  const newArr = data.toString().split('.'); // 分割成数组
  let returnStr = lastIntPart + floatPart; // 默认返回值
  // 小数拼接
  if (newArr.length === 2) {
    floatPart = '.' + newArr[1].toString().padEnd(decimals, '0');
    let fStr = isF && !intPart ? '-' : '';
    const maxFloatLength = cutDecimals ?? decimals;
    if (isCut && floatPart.length - 1 > maxFloatLength) {
      floatPart = floatPart.slice(0, maxFloatLength + 1);
    }
    returnStr = fStr + lastIntPart + floatPart;
  }
  return prefix + returnStr + suffix;
}

/**
 * 日期格式化
 * @param {Date} date 日期
 * @param {String} format 格式
 */
export function formatDate(date, format = dateFormat) {
  if (date) {
    let temp = dayjs(date).format(format);
    return temp == 'Invalid Date' ? '' : temp;
  }
  return '';
}

/**
 * 日期禁用（当前日期之前）
 * @param {Date} date 当前选择的日期
 * @param {Date} current 需要比对的日期
 */
export function disabledDateBefore(date, current = dayjs()) {
  return dayjs(date).isAfter(current);
}

/**
 * 日期禁用（当前日期之后）
 * @param {Date} date 当前选择的日期
 * @param {Date} current 需要比对的日期
 */
export function disabledDateAfter(date, current = dayjs()) {
  return dayjs(date).add(1, 'day').isBefore(current);
}

/**
 * 时间禁用（匹配的时间之前）
 * @param {String} current 当前选择的时间（00:00）
 * @param {String} target 匹配的时间（00:00）
 * @param {Number} hour 时间总长，小时制（24/12），分钟（60）
 */
export function disabledTimeBefore(current, target, hour = 24) {
  if (!target) return [];
  const targetHour = target.split(':')[0] * 1;
  const targetTime = target.split(':')[1] * 1;
  const disabledTimes = [];
  // 小时制，选择后把target之前的全都禁用
  if (hour === 24 || hour === 12) {
    // 时间选择了00，当前小时已无时间可选，直接禁用
    if (targetTime === 0) {
      disabledTimes.push(targetHour);
    }
    for (let time = 0; time < hour; time++) {
      if (targetHour < time) {
        disabledTimes.push(time);
      }
    }
  }
  // 分钟，选择后根据小时来判断是否禁用之前所有的分钟
  if (hour === 60 && current) {
    const currentHour = current.split(':')[0] * 1;
    // 时间不是同一个，不禁用
    if (targetHour !== currentHour) return [];
    for (let time = 0; time < hour; time++) {
      if (targetTime <= time) {
        disabledTimes.push(time);
      }
    }
  }
  return disabledTimes;
}

/**
 * 时间禁用（当前时间之后）
 * @param {Time} current 当前选择的时间（00:00）
 * @param {Time} target 匹配的时间（00:00）
 * @param {Number} hour 时间总长，小时制（24/12），分钟（60）
 */
export function disabledTimeAfter(current, target, hour = 24) {
  if (!target) return [];
  const targetHour = target.split(':')[0] * 1;
  const targetTime = target.split(':')[1] * 1;
  const disabledTimes = [];
  // 小时制，选择后把target之前的全都禁用
  if (hour === 24 || hour === 12) {
    // 时间选择了59，当前小时已无时间可选，直接禁用
    if (targetTime === 59) {
      disabledTimes.push(targetHour);
    }
    for (let time = 0; time < hour; time++) {
      if (targetHour > time) {
        disabledTimes.push(time);
      }
    }
  }
  // 分钟，选择后根据小时来判断是否禁用之前所有的分钟
  if (hour === 60 && current) {
    const currentHour = current.split(':')[0] * 1;
    // 时间不是同一个，不禁用
    if (targetHour !== currentHour) return [];
    for (let time = 0; time < hour; time++) {
      if (targetTime >= time) {
        disabledTimes.push(time);
      }
    }
  }
  return disabledTimes;
}

/**
 * 年龄过滤
 * @param {Date} birthday 生日
 * @param {Boolean} format 格式化
 * @returns {String} 返回年月日
 */
export function filterBirthday(birthday, format) {
  birthday = dayjs(birthday).format(dateFormat + ' 00:00:00');
  let year = 0;
  let month = 0;
  let day = 0;
  if (birthday) {
    year = dayjs().diff(birthday, 'year');
    month = dayjs().subtract(year, 'year').diff(birthday, 'month');
    day = dayjs()
      .subtract(year, 'year')
      .subtract(month, 'month')
      .diff(birthday, 'day');
  }
  return format
    ? { year, month, day }
    : year + t('common.birth_year_simple') + month + t('common.birth_month_simple') + day + t('common.birth_day_simple');
}

/**
 * 手机号正则校验
 * @param {String} phone 手机号
 */
export function phoneReg(phone) {
  // 国际版不做校验
  return true;
  // const reg = /^(?:(?:\+|00)86)?1(?:(?:3[\d])|(?:4[5-79])|(?:5[0-35-9])|(?:6[5-7])|(?:7[0-8])|(?:8[\d])|(?:9[1589]))\d{8}$/;

  // 2024.3.14修改正则，开头是1，后十位不用管即可
  const reg = /^1\d{10}$/;
  return reg.test(phone);
}

/**
 * 密码校验
 * @param {String} password 密码
 */
export function passwordReg(password) {
  const reg =
    /^(?=.*\d)(?=.*[a-zA-Z])(?=.*[!@#$%^&*()_+=\.\-])(?=\S+$).{6,20}$/g;
  return reg.test(password);
}

/**
 * 中国大陆身份证正则校验
 * @param {String} idCard 身份证号
 */
export function idCardReg(code) {
  // 国际版不做校验
  return true;
  const reg =
    /^(^[1-9]\d{7}((0\d)|(1[0-2]))(([0|1|2]\d)|3[0-1])\d{3}$)|(^[1-9]\d{5}[1-9]\d{3}((0\d)|(1[0-2]))(([0|1|2]\d)|3[0-1])((\d{4})|\d{3}[Xx])$)$/;
  let row = true;
  const city = {
    11: '北京',
    12: '天津',
    13: '河北',
    14: '山西',
    15: '内蒙古',
    21: '辽宁',
    22: '吉林',
    23: '黑龙江',
    31: '上海',
    32: '江苏',
    33: '浙江',
    34: '安徽',
    35: '福建',
    36: '江西',
    37: '山东',
    41: '河南',
    42: '湖北',
    43: '湖南',
    44: '广东',
    45: '广西',
    46: '海南',
    50: '重庆',
    51: '四川',
    52: '贵州',
    53: '云南',
    54: '西藏',
    61: '陕西',
    62: '甘肃',
    63: '青海',
    64: '宁夏',
    65: '新疆',
    71: '台湾',
    81: '香港',
    82: '澳门',
    91: '国外',
  };
  if (!code || !reg.test(code)) {
    console.log('身份证校验', '正则未通过');
    row = false;
  } else if (!city[code.substr(0, 2)]) {
    console.log('身份证校验', '城市未通过');
    row = false;
  } else {
    // 18位身份证需要验证最后一位校验位
    // if (code.length == 18) {
    //   code = code.split('');
    //   //∑(ai×Wi)(mod 11)
    //   //加权因子
    //   var factor = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2];
    //   //校验位
    //   var parity = [1, 0, 'X', 9, 8, 7, 6, 5, 4, 3, 2];
    //   var sum = 0;
    //   var ai = 0;
    //   var wi = 0;
    //   for (var i = 0; i < 17; i++) {
    //     ai = code[i];
    //     wi = factor[i];
    //     sum += ai * wi;
    //   }
    //   if (parity[sum % 11] != code[17].toUpperCase()) {
    //     console.log('身份证校验', '校验位未通过');
    //     row = false;
    //   }
    // }
  }
  return row;
}

/**
 * 中国大陆身份证获取出生年月和年龄
 * @param {String} idCard 身份证号
 * @param {String} split 分隔符
 */
export function getAgeByIdCard(idCard, split = '-') {
  if (!idCard) return false;
  const dataObj = {
    gender: null, // 性别 0未知 1男 2女 9未说明
    year: null, // 出生年
    month: null, // 出生月
    day: null, // 出生日
    birth: null, // 年月日
  };
  // 校验未通过
  if (!idCardReg(idCard)) return dataObj;
  // 截取生日
  const birthday = idCard.substring(6, 14);
  const year = birthday.substring(0, 4);
  const month = birthday.substring(4, 6);
  const day = birthday.substring(6);
  dataObj.year = Number(year);
  dataObj.month = Number(month);
  dataObj.day = Number(day);
  dataObj.birth = formatDate([year, month, day].join(split));
  // 截取性别位
  const genderNum = idCard.substring(16, 17);
  dataObj.gender = genderNum % 2 ? 1 : 2;
  console.log(dataObj);
  return dataObj;
}

/**
 * ip正则校验(无端口)
 */
export function ipNoPortReg(ip) {
  const reg =
    /^(\d|[1-9]\d|1\d{2}|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d{2}|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d{2}|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d{2}|2[0-4]\d|25[0-5])$/g;
  return reg.test(ip);
}

/**
 * 端口校验
 */
export function portReg(port) {
  const reg =
    /^[0-9]|[1-9]\d|[1-9]\d{2}|[1-9]\d{3}|[1-5]\d{4}|6[0-4]\d{3}|65[0-4]\d{2}|655[0-2]\d|6553[0-5]$/g;
  return reg.test(port);
}

/**
 * 血压格式的正则校验
 * xx/xx
 */
export function bloodReg(str) {
  const reg = /^(?!0)\d+\/(?!0)\d+$/; // 不可为0
  // const reg = /^\d+\/\d+$/; // 可为0
  return reg.test(str);
}

/**
 * 拼音转换
 * @param {String} str 字符串
 */
export function pinyinFilter(str) {
  return pinyin(str, { pattern: 'first', toneType: 'none' })
    .replace(/\s*/g, '')
    .toLocaleUpperCase();
}

/**
 * 时间段重合校验
 * @param {Array[Date]} first 第一个时间段
 * @param {Array[Date]} second 第二个时间段
 */
export function timeRangeFilter(first, second) {
  const [firstStart, firstEnd] = first;
  const [secondStart, secondEnd] = second;
  return (
    dayjs(firstStart).isBefore(secondEnd) &&
    dayjs(firstEnd).isAfter(secondStart)
  );
}

/**
 * 文本是否全都是空格
 * @param {String} str 字符串
 */
export function isAllSpace(str) {
  return String(str).replace(/\s*/g, '').length === 0;
}

/**
 * 数字根据小数点切割成两位，不向上或向下
 */
Number.prototype.toSlice = function (n = 2) {
  const [first, second] = String(this).split('.');
  return second ? Number(first + '.' + second.slice(0, n)) : Number(first);
};

/**
 * 数组根据小数点切割成两位，不向上或向下，返回多出的部分并拼接
 */
Number.prototype.toSliceMore = function (n = 2) {
  const [first, second] = String(this).split('.');
  if (second) {
    // 除数
    const divisor = Number(second.slice(n));
    // 被除数
    const dividend = Number('1' + ''.padEnd(second.length, '0'));
    console.log(divisor, dividend, divisor / dividend)
    return divisor / dividend;
  }
  return 0;
};

/**
 * 数字判断是否是小数
 */
Number.prototype.isFloat = function () {
  const reg = /[\.]/;
  return reg.test(this);
};

/**
 * 数字四舍五入
 */
Number.prototype.toRound = function (n = 2) {
  return _.round(this, n);
};

/**
 * 字符串保留前n位进行切割并加省略号
 */
String.prototype.toSplice = function (n = 6) {
  return this.slice(0, n) + (this.length >= n ? '...' : '');
};

/**
 * 首字母大写
 */
String.prototype.toUpperFirstCase = function () {
  return this.charAt(0).toUpperCase() + this.slice(1);
};

/**
 * 数字百分比化
 * @param {Number} num 字符串
 * @param {Number} decimals 小数位数
 */
export function formatPer(num, decimals = 2) {
  return (num * 100).toFixed(decimals) + '%';
}

/**
 * 手机号加密处理
 * @param {String} tel 手机号
 */
export function telEncrypt(tel) {
  if (!tel) return '';
  return tel.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2');
}

/**
 * 功能权限校验
 * @param {String} Name 权限节点
 */
export function permissionCheck(name) {
  const { getPermission } = menuStore();
  let flag = true;
  const list = getPermission();
  flag = list.includes(name);
  return flag;
}
